/**
 * InputEditor - Rich Text Editor Component
 *
 * A comprehensive rich text editor built with Slate.js that provides:
 * - Text formatting (bold, italic)
 * - Block formatting (alignment, lists, headings)
 * - Link support with inline editing
 * - Indentation controls
 * - Undo/redo functionality
 * - Responsive toolbar design
 * - HTML serialization and parsing
 * - Formik integration
 *
 * Features:
 * - Preserves line breaks between paragraphs
 * - Maintains link integrity during editing
 * - Mobile-responsive toolbar with dropdown menus
 * - Proper keyboard navigation and accessibility
 * - Clean HTML output with inline styles
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormHelperText,
  Box,
  Toolbar,
  Divider,
  useMediaQuery,
  useTheme,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import {
  createEditor,
  Descendant,
  Editor,
  Transforms,
  Element as SlateElement,
  Range,
} from 'slate';
import { withHistory } from 'slate-history';
import { Slate, Editable, withReact } from 'slate-react';
import {
  FormatBold,
  FormatItalic,
  FormatIndentIncrease,
  FormatIndentDecrease,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Title,
  Link,
  Undo,
  Redo,
} from '@mui/icons-material';

// Import our modular components and utilities
import { InputEditorProps } from './input-editor.types';
import { htmlToSlate, slateToHtml } from './input-editor.serialization';
import {
  toggleMark,
  toggleBlock,
  insertLink,
  increaseIndent,
  decreaseIndent,
} from './input-editor.utils';
import {
  MarkButton,
  BlockButton,
  UndoRedoButton,
  LinkButton,
  IndentButton,
  AlignmentMenuButton,
  ListMenuButton,
  MoreMenuButton,
} from './input-editor.toolbar';

// ============================================================================
// SLATE ELEMENT AND TEXT RENDERING COMPONENTS
// ============================================================================

/**
 * Element renderer for different block-level elements
 *
 * Handles rendering of:
 * - Paragraphs (with alignment and indentation)
 * - Headings
 * - Lists (bulleted and numbered)
 * - List items
 * - Links (inline elements)
 */
const Element = ({ attributes, children, element }: any) => {
  // Build inline styles for alignment and indentation
  const style: React.CSSProperties = {};

  if (element.align) {
    style.textAlign = element.align;
  }

  if (element.indent && element.indent > 0) {
    style.marginLeft = `${element.indent * 20}px`; // 20px per indent level
  }

  switch (element.type) {
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'heading':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'link':
      return (
        <a
          {...attributes}
          href={element.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline' }}
        >
          {children}
        </a>
      );
    default:
      // Default to paragraph for all other element types
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

/**
 * Leaf renderer for inline text formatting
 *
 * Handles text-level formatting like bold and italic
 */
const Leaf = ({ attributes, children, leaf }: any) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  return <span {...attributes}>{children}</span>;
};

// ============================================================================
// EDITOR PLUGINS AND INITIALIZATION
// ============================================================================

/**
 * Slate plugin to handle link elements as inline elements
 *
 * This plugin ensures that:
 * - Links are treated as inline elements (not block elements)
 * - Links always have at least one text child for proper editor behavior
 * - Normalization maintains link structure integrity
 */
const withLinks = (editor: Editor) => {
  const { isInline, normalizeNode } = editor;

  // Register links as inline elements
  editor.isInline = (element: any) => {
    return element.type === 'link' ? true : isInline(element);
  };

  // Ensure proper link structure during normalization
  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    // Ensure links have at least one text child
    if (SlateElement.isElement(node) && node.type === 'link') {
      if (node.children.length === 0) {
        Transforms.insertNodes(editor, { text: '' }, { at: [...path, 0] });
        return;
      }
    }

    normalizeNode(entry);
  };

  return editor;
};

/**
 * Main InputEditor Component
 *
 * A rich text editor built on Slate.js with Material-UI integration.
 *
 * Features:
 * - Rich text formatting (bold, italic)
 * - Block elements (headings, lists)
 * - Text alignment and indentation
 * - Link support with HTML serialization
 * - Responsive toolbar with mobile menu
 * - Formik integration for form handling
 * - Keyboard shortcuts for common actions
 *
 * @param value - Initial content (HTML string or Slate nodes)
 * @param onChange - Callback when content changes (returns HTML string)
 * @param placeholder - Placeholder text for empty editor
 * @param autoFocus - Whether to focus editor on mount
 * @param minHeight - Minimum height of editor content area (px)
 * @param maxHeight - Maximum height of editor content area (px)
 * @param showToolbar - Whether to display formatting toolbar
 * @param error - Error message to display
 * @param disabled - Whether editor is read-only
 * @param formik - Formik form instance for integration
 * @param field - Formik field configuration
 */
export const InputEditor: React.FC<InputEditorProps> = ({
  value: initialValue,
  onChange,
  placeholder = 'Start typing...',
  autoFocus = false,
  minHeight = 150,
  maxHeight = 400,
  showToolbar = true,
  theme = 'default',
  error,
  disabled = false,
  field,
  nameOverride,
  ...props
}) => {
  const theme_ = useTheme();
  const formik = useFormikContext<any>();

  // Initialize editor with plugins
  const renderElement = useCallback((props: any) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);
  const editor = useMemo(
    () => withHistory(withReact(withLinks(createEditor()))),
    [],
  );

  // Initialize state with proper value parsing
  const [value, setValue] = useState<Descendant[]>(() => {
    if (initialValue) {
      if (typeof initialValue === 'string') {
        return htmlToSlate(initialValue);
      } else {
        return initialValue;
      }
    }
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  });

  const [isFocused, setIsFocused] = useState(false);

  // Update state when initialValue changes
  useEffect(() => {
    if (initialValue) {
      if (typeof initialValue === 'string') {
        const slateValue = htmlToSlate(initialValue);
        setValue(slateValue);
      } else {
        setValue(initialValue);
      }
    } else {
      setValue([{ type: 'paragraph', children: [{ text: '' }] }]);
    }
  }, [initialValue]);

  // Handle editor changes with HTML serialization
  const handleChange = useCallback(
    (newValue: Descendant[]) => {
      setValue(newValue);

      // Convert to HTML and call onChange
      const htmlValue = slateToHtml(newValue);

      if (onChange) {
        onChange(htmlValue);
      }

      // Update formik field if provided
      if (formik && nameOverride) {
        formik.setFieldValue(nameOverride, htmlValue);
      }
    },
    [onChange, formik, nameOverride],
  );

  // Handle keyboard shortcuts
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;

      switch (event.key) {
        case 'b':
          event.preventDefault();
          toggleMark(editor, 'bold');
          break;
        case 'i':
          event.preventDefault();
          toggleMark(editor, 'italic');
          break;
        case 'k':
          event.preventDefault();
          const url = window.prompt('Enter the URL:');
          if (url) {
            insertLink(editor, url);
          }
          break;
        case ']':
          event.preventDefault();
          increaseIndent(editor);
          break;
        case '[':
          event.preventDefault();
          decreaseIndent(editor);
          break;
        default:
          break;
      }
    },
    [editor],
  );

  // Get error message from props or formik
  const errorMessage =
    error ||
    (formik &&
      field &&
      formik.errors[field.name] &&
      formik.touched[field.name] &&
      formik.errors[field.name]);

  return (
    <Box
      sx={{
        border: `1px solid ${error || errorMessage ? theme_.palette.error.main : theme_.palette.divider}`,
        borderRadius: 1,
        backgroundColor: disabled
          ? theme_.palette.action.disabledBackground
          : 'background.paper',
        opacity: disabled ? 0.6 : 1,
        '&:focus-within': {
          borderColor: error || errorMessage ? 'error.main' : 'primary.main',
          borderWidth: 2,
        },
      }}
      {...props}
    >
      <Slate editor={editor} initialValue={value} onChange={handleChange}>
        {/* Responsive toolbar with formatting controls */}
        {showToolbar && !disabled && (
          <Box
            sx={{
              p: 1,
              borderBottom: `1px solid ${theme_.palette.divider}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              alignItems: 'center',
            }}
          >
            {/* Text formatting */}
            <MarkButton format="bold" icon={FormatBold} />
            <MarkButton format="italic" icon={FormatItalic} />

            <Divider orientation="vertical" flexItem />

            {/* Lists */}
            <BlockButton format="bulleted-list" icon={FormatListBulleted} />
            <BlockButton format="numbered-list" icon={FormatListNumbered} />

            <Divider orientation="vertical" flexItem />

            {/* Alignment */}
            <BlockButton format="left" icon={FormatAlignLeft} />
            <BlockButton format="center" icon={FormatAlignCenter} />
            <BlockButton format="right" icon={FormatAlignRight} />

            <Divider orientation="vertical" flexItem />

            {/* Indentation */}
            <IndentButton action="decrease" icon={FormatIndentDecrease} />
            <IndentButton action="increase" icon={FormatIndentIncrease} />

            <Divider orientation="vertical" flexItem />

            {/* Links */}
            <LinkButton />

            {/* Additional options in responsive menu */}
            <MoreMenuButton />
          </Box>
        )}

        {/* Editor content area */}
        <Box
          sx={{
            p: 2,
            minHeight: minHeight,
            maxHeight: maxHeight,
            overflow: 'auto',
          }}
        >
          <Editable
            readOnly={disabled}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            autoFocus={autoFocus}
            style={{ outline: 'none', minHeight: '100%' }}
          />
        </Box>
      </Slate>

      {/* Error display */}
      {(error || errorMessage) && (
        <Box sx={{ p: 1, pt: 0.5 }}>
          <Typography variant="caption" color="error">
            {error || errorMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InputEditor;
